'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useAuth } from '@/contexts';
import { useGetSearchProfile } from '@/queris';
import { getFiltersFromProfileSearch } from '@/common';
import { PATHS } from '@/constants';
import { FiltersType } from '@/types';
import { useSearchParamsCompat } from './useSearchParamsCompat';

type UseHydrateSearchProfileParams = {
  initialSearchProfileId?: string | null;
  setFilters: (patch: Partial<FiltersType>) => void;
  onBeforeApply?: () => void;
};

export function useHydrateSearchProfile({
  initialSearchProfileId,
  setFilters,
  onBeforeApply,
}: UseHydrateSearchProfileParams) {
  const searchParams = useSearchParamsCompat();
  const searchProfileId = searchParams.get(PATHS.SEARCH_PROFILE) ?? initialSearchProfileId ?? null;

  const { isAuthenticated } = useAuth();
  const { status } = useSession();

  const {
    data: profileSearch,
    isLoading: isProfileSearchLoading,
    isError,
  } = useGetSearchProfile(searchProfileId);

  const [appliedProfileId, setAppliedProfileId] = useState<string | null>(null);

  useEffect(() => {
    setAppliedProfileId(null);
  }, [searchProfileId]);

  useEffect(() => {
    if (!searchProfileId || !profileSearch?.filters) return;
    if (appliedProfileId === searchProfileId) return;
    onBeforeApply?.();
    setFilters({
      ...getFiltersFromProfileSearch(profileSearch.filters),
      tabs: profileSearch.viewType,
    });
    setAppliedProfileId(searchProfileId);
  }, [profileSearch, searchProfileId, appliedProfileId, setFilters, onBeforeApply]);

  // A guest with a saved-profile id in the URL sees the ordinary search.
  // The profile is applied only after a session exists.
  const isProfileReady =
    !searchProfileId ||
    (status !== 'loading' && !isAuthenticated) ||
    isError ||
    appliedProfileId === searchProfileId;

  return { searchProfileId, isProfileReady, isProfileSearchLoading };
}
